import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  fetchAdminPolicies,
  updateAdminPolicy,
  POLICY_LABELS,
  TYPE_TO_HANDLE,
  type PolicyType,
  type ShopPolicy,
} from '@/lib/shopifyPolicies';

const EDITABLE_TYPES: PolicyType[] = [
  'PRIVACY_POLICY',
  'REFUND_POLICY',
  'TERMS_OF_SERVICE',
  'SHIPPING_POLICY',
  'SUBSCRIPTION_POLICY',
  'LEGAL_NOTICE',
  'CONTACT_INFORMATION',
];

export default function AdminPolicies() {
  const [policies, setPolicies] = useState<Record<PolicyType, ShopPolicy>>({} as any);
  const [drafts, setDrafts] = useState<Record<PolicyType, string>>({} as any);
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState<PolicyType | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await fetchAdminPolicies();
      const map = {} as Record<PolicyType, ShopPolicy>;
      const draftMap = {} as Record<PolicyType, string>;
      // start from existing list
      list.forEach((p) => {
        map[p.type] = p;
        draftMap[p.type] = p.body;
      });
      // ensure every editable type has a draft (Shopify creates the policy on first save)
      EDITABLE_TYPES.forEach((t) => {
        if (!(t in map)) {
          map[t] = { type: t, title: POLICY_LABELS[t], body: '' };
          draftMap[t] = '';
        }
      });
      setPolicies(map);
      setDrafts(draftMap);
    } catch (e: any) {
      toast.error('שגיאה בטעינת המדיניות: ' + (e?.message ?? 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (type: PolicyType) => {
    setSavingType(type);
    try {
      await updateAdminPolicy(type, drafts[type] ?? '');
      toast.success(`"${POLICY_LABELS[type]}" נשמר ועודכן ב-Shopify`);
      // refresh that policy's url/id
      await load();
    } catch (e: any) {
      toast.error('שמירה נכשלה: ' + (e?.message ?? 'unknown'));
    } finally {
      setSavingType(null);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">טוען מדיניות מ-Shopify…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold">מדיניות החנות</h1>
        <p className="text-sm text-muted-foreground mt-1">
          מדיניות זו נשלפת ישירות מ-Shopify. שינויים נשמרים אצל Shopify ומשם מוצגים באתר.
          ניתן להשתמש ב-HTML בסיסי (כותרות, פסקאות, רשימות, קישורים).
        </p>
      </div>

      <div className="space-y-8">
        {EDITABLE_TYPES.map((type) => {
          const p = policies[type];
          const handle = TYPE_TO_HANDLE[type];
          return (
            <section key={type} className="border rounded-lg p-5 bg-white space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-semibold">{POLICY_LABELS[type]}</h2>
                  <div className="text-xs text-muted-foreground mt-0.5" dir="ltr">
                    /policies/{handle}
                    {p?.url && (
                      <>
                        {' · '}
                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="underline">
                          תצוגה ב-Shopify
                        </a>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => save(type)}
                  disabled={savingType !== null}
                  size="sm"
                >
                  {savingType === type ? 'שומר…' : 'שמירה ל-Shopify'}
                </Button>
              </div>

              <div>
                <Label className="sr-only">תוכן</Label>
                <Textarea
                  value={drafts[type] ?? ''}
                  onChange={(e) => setDrafts((d) => ({ ...d, [type]: e.target.value }))}
                  rows={14}
                  dir="rtl"
                  className="font-mono text-sm"
                  placeholder="התוכן יוצג באתר ובחנות Shopify"
                />
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
